import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import appCss from "../styles.css?url";
const ogCover = "https://xenomons.online/og-cover.jpg";
import { reportLovableError, copyErrorToClipboard, installGlobalErrorListeners } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { INVALID_SESSION_EVENT, handleInvalidSession, isAuthError, isExpiredSession, type InvalidSessionReason } from "@/lib/auth/invalid-session";
import { claimActiveSession, clearLocalSessionId, getLocalSessionId } from "@/lib/auth/session-id";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { applyDetectedLanguage } from "@/i18n";
import "@/i18n";
import { afterFirstPageRender } from "@/lib/first-page-render";

function NotFoundComponent() {
  const { t } = useTranslation("common");
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-glow">{t("errors.notFoundTitle")}</h1>
        <p className="mt-4 text-muted-foreground">{t("errors.notFoundBody")}</p>
        <Link to="/" className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-primary-foreground shadow-glow">
          {t("errors.backToGame")}
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">{t("errors.generic")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground shadow-glow"
          >
            {t("actions.retry")}
          </button>
          <button
            onClick={async () => {
              const ok = await copyErrorToClipboard(error, { boundary: "tanstack_root_error_component" });
              if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000); }
            }}
            className="rounded-md border border-border bg-card/70 px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
          >
            {copied ? t("errors.copied") : t("errors.copyDetails")}
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#0a0a14" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "Xenomons" },
      { title: "Xenomons – Cyber Creature Battler" },
      { name: "description", content: "Ziehe dein einzigartiges Xenomon groß. KI-generierte Gegner, taktische Kämpfe, Zucht und Entfaltung im Cyber-Habitat." },
      { name: "author", content: "Xenomons" },
      { property: "og:site_name", content: "Xenomons" },
      { property: "og:title", content: "Xenomons – Cyber Creature Battler" },
      { property: "og:description", content: "Ziehe dein einzigartiges Xenomon groß. KI-generierte Gegner, taktische Kämpfe, Zucht und Entfaltung im Cyber-Habitat." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Xenomons – Cyber Creature Battler" },
      { name: "twitter:description", content: "Ziehe dein einzigartiges Xenomon groß. KI-generierte Gegner, taktische Kämpfe, Zucht und Entfaltung im Cyber-Habitat." },
      { property: "og:image", content: ogCover },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "640" },
      { name: "twitter:image", content: ogCover },
      { name: "google-site-verification", content: "1sWwgqG1ogJhZL3t9Pualp924a30AJnVw-wftkoHadQ" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Lilita+One&family=Nunito:wght@600;700;800&display=swap" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="de">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthSync() {
  const router = useRouter();
  const qc = useQueryClient();
  useEffect(() => {
    installGlobalErrorListeners();
    async function forceSignOut() {
      await qc.cancelQueries().catch(() => undefined);
      qc.clear();
      await router.navigate({ to: "/auth", replace: true }).catch(() => undefined);
    }

    async function validateSession() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) return;

      if (isExpiredSession(sessionData.session)) {
        await handleInvalidSession();
        return;
      }

      const { error } = await supabase.auth.getUser();
      if (error && isAuthError(error)) {
        await handleInvalidSession();
      }
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Where a password-recovery link lands is decided by the mail template,
      // not by us. Wherever it lands, Supabase signs the visitor in and fires
      // this event only after it has read the fragment — so this is the safe
      // moment to route them to the form. Without it the landing and login
      // guards bounce them into the game and the password is never changed.
      if (event === "PASSWORD_RECOVERY") {
        if (window.location.pathname !== "/reset-password") {
          void router.navigate({ to: "/reset-password", replace: true }).catch(() => undefined);
        }
        return;
      }
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      // Sync Sentry user context (no-op if DSN not configured).
      void import("@/lib/sentry-browser").then(({ setSentryUser }) => {
        setSentryUser(event === "SIGNED_OUT" ? null : session?.user?.id ?? null);
      });
      if (event === "SIGNED_OUT") {
        clearLocalSessionId();
        // Navigate away BEFORE clearing/invalidating — otherwise the still-mounted
        // protected route loaders refire without a bearer token and 401.
        void qc.cancelQueries().catch(() => undefined).then(() => {
          qc.clear();
        });
        void router.navigate({ to: "/auth", replace: true }).catch(() => undefined);
        return;
      }

      router.invalidate();
      // On fresh sign-in, drop any stale "my-monster" cache from a prior
      // (possibly anonymous) session so GameGate cannot briefly act on a
      // pre-login `{ monster: null }` snapshot.
      if (event === "SIGNED_IN") {
        // Supabase feuert SIGNED_IN auch bei Tab-Refokus/Re-Hydration erneut.
        // Nur bei echtem frischem Login (keine lokale Session-ID vorhanden)
        // neu claimen — sonst supersedet sich die Session bei Tab-Wechsel selbst.
        if (!getLocalSessionId()) {
          qc.removeQueries({ queryKey: ["my-monster"] });
          // Claim this device as the active session BEFORE any protected server-fn
          // call fires. Failure-safe: localStorage is only written on RPC success,
          // so a failed claim never locks the user out.
          void claimActiveSession().catch((err) => {
            console.warn("[active-session] claim error", err);
          });
        }
      }
      qc.invalidateQueries();
      // Persist marketing consent stashed by the signup flow (password or Google OAuth redirect).
      if (event === "SIGNED_IN" && typeof window !== "undefined") {
        try {
          const pending = sessionStorage.getItem("xeno_marketing_pending");
          if (pending !== null) {
            sessionStorage.removeItem("xeno_marketing_pending");
            // Imported lazily: this runs browser-only after sign-in, and a
            // top-level import would put a server-function module into the
            // root route's load-time graph for every request.
            void import("@/lib/auth/consent.functions")
              .then(({ recordMarketingConsent }) =>
                recordMarketingConsent({ data: { consent: pending === "1" } }),
              )
              .catch((err) => {
                console.warn("[marketing-consent] persist failed", err);
              });
          }
        } catch {
          /* ignore */
        }
      }
    });

    void validateSession().catch(() => undefined);

    function onInvalidSession(ev: Event) {
      const reason = (ev as CustomEvent<{ reason?: InvalidSessionReason }>).detail?.reason;
      if (reason === "superseded") {
        toast.error(
          "Du wurdest abgemeldet, weil dein Account auf einem anderen Gerät angemeldet wurde.",
          { duration: 8000 },
        );
      }
      void forceSignOut();
    }

    function onVisible() {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        void validateSession().catch(() => undefined);
      }
    }

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.addEventListener(INVALID_SESSION_EVENT, onInvalidSession);
    }

    // Poll every 60s: if the local session has expired (e.g. because the
    // refresh_token endpoint kept failing with "Failed to fetch" after a
    // backend restart), force sign-out instead of letting background pollers
    // spin against a dead session.
    const interval = typeof window !== "undefined"
      ? window.setInterval(() => { void validateSession().catch(() => undefined); }, 60_000)
      : null;

    return () => {
      subscription.unsubscribe();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener(INVALID_SESSION_EVENT, onInvalidSession);
      }
      if (interval !== null && typeof window !== "undefined") window.clearInterval(interval);
    };
  }, [router, qc]);
  return null;
}

function HtmlLangSync() {
  const { i18n } = useTranslation();
  const router = useRouter();
  useEffect(() => {
    // Die bevorzugte Sprache erst anwenden, wenn auch der SEITENINHALT hydriert
    // ist. Dieser Effekt läuft schon nach der Root-Route; hier direkt
    // umzuschalten ließ jede öffentliche Seite auf Englisch hydrieren, obwohl
    // der Server Deutsch geschickt hatte (React #418, siehe first-page-render.ts).
    return afterFirstPageRender(router, applyDetectedLanguage);
  }, [router]);
  useEffect(() => {
    if (typeof document === "undefined") return;
    const apply = () =>
      document.documentElement.setAttribute("lang", i18n.resolvedLanguage ?? "de");
    apply();
    i18n.on("languageChanged", apply);
    return () => i18n.off("languageChanged", apply);
  }, [i18n]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={150}>
        <HtmlLangSync />
        <AuthSync />
        <Outlet />
        <Toaster />
        
      </TooltipProvider>
    </QueryClientProvider>
  );
}
